// src/pages/LiveConversation/LiveConversation.js
import React, { useState, useEffect, useCallback } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';
import { hasPermission } from '../../../utils/permissionUtils';
import TiptapEditor from '../../../components/Common/TiptapEditor';

import {
    Box,
    Typography,
    Alert,
    Button,
    Switch,
    TextField,
    CircularProgress,
    Card,
    CardContent,
    Tabs,
    Tab,
    Checkbox,
    LinearProgress,
    Chip,
    IconButton,
    Divider,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Save as SaveIcon,
    Language as WebIcon,
    Description as DocIcon,
    CloudQueue as DriveIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    Build as ToolIcon,
    Code as VarIcon,
    AddCircleOutline as AddIcon,
    RecordVoiceOver as RecordVoiceOverIcon,
    ArrowForward as NextIcon,
    ArrowBack as BackIcon,
    CheckCircle as DoneIcon,
    ReceiptLong as LogsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const MAX_CHARACTERS = 20000;
const STEP_LABELS = ['General', 'Knowledge & sources', 'Variables'];

export default function LiveConversation() {
    const navigate = useNavigate();
    const { chatbotId: urlChatbotId } = useParams();
    const { activeChatbot } = useChatbots();
    const chatbotId = activeChatbot?.chatbot_identity;

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Active setup step / tab (0 = General, 1 = Knowledge & sources, 2 = Variables)
    const [tab, setTab] = useState(0);

    // Data states
    const [ragEnabled, setRagEnabled] = useState(true);
    const [greetingMessage, setGreetingMessage] = useState('Hello, how can I help you?');
    const [promptText, setPromptText] = useState('');
    const [selectedSources, setSelectedSources] = useState([]);
    // VAPI-style variables: list of {key, value} rows that resolve {{var}}
    // placeholders in the prompt and tool body templates at runtime.
    const [variableRows, setVariableRows] = useState([]);
    const [availableSources, setAvailableSources] = useState([]);
    const [sourceTab, setSourceTab] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingSources, setLoadingSources] = useState(false);
    const [message, setMessage] = useState(null);
    // Per-chatbot voice provider ('' = use the klonit .env default).
    const [sttProvider, setSttProvider] = useState('');
    const [totalSourcePage, setTotalSourcePage] = useState(0);
    const [sourcePage, setSourcePage] = useState(1);

    const sourceTypes = ['website', 'document', 'gdrive'];
    const sourceTabLabels = ['Website', 'Documents', 'Google Drive'];
    const sourceTabIcons = [<WebIcon />, <DocIcon />, <DriveIcon />];

    // Tiptap editor for manual content
    const editor = useEditor({
        extensions: [StarterKit, Underline],
        content: '',
        editorProps: {
            attributes: {
                class: 'tiptap-editor-area',
            },
        },
    });

    // Calculate total character count
    const manualContentLength = editor ? editor.getText().length : 0;
    const selectedSourcesChars = selectedSources.reduce((sum, s) => sum + (s.character_count || 0), 0);
    const totalCharacters = manualContentLength + selectedSourcesChars;
    const characterPercentage = Math.min((totalCharacters / MAX_CHARACTERS) * 100, 100);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('live-conversation', 'view');
                const modifyPerm = await hasPermission('live-conversation', 'add|edit');
                setCanView(viewPerm);
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };
        checkPermissions();
    }, []);

    // Fetch existing settings
    useEffect(() => {
        if (!chatbotId || !canView) return;
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-conversation`);
                const data = response.data?.data;
                if (data) {
                    setRagEnabled(data.rag_enabled);
                    setGreetingMessage(data.greeting_message || 'Hello, how can I help you?');
                    setPromptText(data.prompt_text || '');
                    setSelectedSources(data.sources || []);
                    setSttProvider(data.stt_provider || '');
                    const vv = data.variable_values && typeof data.variable_values === 'object'
                        ? data.variable_values : {};
                    setVariableRows(
                        Object.entries(vv).map(([key, value]) => ({ key, value: String(value ?? '') }))
                    );
                    if (editor && data.manual_content) {
                        editor.commands.setContent(data.manual_content);
                    }
                }
            } catch (error) {
                console.error('Error fetching live conversation settings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [chatbotId, canView]);

    // Set editor content when editor becomes available and we have saved content
    useEffect(() => {
        if (editor && !loading) {
            // Editor ready - content already set during fetch
        }
    }, [editor, loading]);

    // Fetch available sources when tab or search changes
    const fetchAvailableSources = useCallback(async (page = 1) => {
        if (!chatbotId) return;
        setLoadingSources(true);
        try {
            const response = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-conversation/sources`, {
                params: {
                    source_type: sourceTypes[sourceTab],
                    page,
                    limit: 20,
                    search: searchText,
                }
            });
            setAvailableSources(response.data?.data || []);
            setTotalSourcePage(Math.ceil((response.data?.total || 0) / 20));
            setSourcePage(page);
        } catch (error) {
            console.error('Error fetching available sources:', error);
            setAvailableSources([]);
        } finally {
            setLoadingSources(false);
        }
    }, [chatbotId, sourceTab, searchText]);

    useEffect(() => {
        if (chatbotId && canView) {
            fetchAvailableSources(1);
        }
    }, [chatbotId, canView, sourceTab, searchText, fetchAvailableSources]);

    // Check if a source is already selected
    const isSourceSelected = (sourceType, sourceId) => {
        return selectedSources.some(s => s.source_type === sourceType && s.source_id === sourceId);
    };

    // Toggle source selection
    const handleToggleSource = (source) => {
        const alreadySelected = isSourceSelected(source.source_type, source.source_id);

        if (alreadySelected) {
            setSelectedSources(prev => prev.filter(
                s => !(s.source_type === source.source_type && s.source_id === source.source_id)
            ));
        } else {
            // Check character limit
            const newTotal = totalCharacters + (source.character_count || 0);
            if (newTotal > MAX_CHARACTERS) {
                setMessage({ type: 'error', text: `Adding this source would exceed the ${MAX_CHARACTERS.toLocaleString()} character limit.` });
                return;
            }
            setSelectedSources(prev => [...prev, {
                source_type: source.source_type,
                source_id: source.source_id,
                source_title: source.source_title,
                content: source.content || '',
                character_count: source.character_count || 0,
            }]);
        }
    };

    // Remove a selected source
    const handleRemoveSource = (sourceType, sourceId) => {
        setSelectedSources(prev => prev.filter(
            s => !(s.source_type === sourceType && s.source_id === sourceId)
        ));
    };

    // Save settings
    const handleSave = async () => {
        if (!chatbotId) return;
        setSaving(true);
        setMessage(null);

        try {
            const manualContent = editor ? editor.getHTML() : '';
            // Convert key/value rows -> dict, dropping rows with empty keys.
            const variableValues = variableRows.reduce((acc, row) => {
                const k = (row.key || '').trim();
                if (k) acc[k] = row.value ?? '';
                return acc;
            }, {});
            const payload = {
                rag_enabled: ragEnabled,
                greeting_message: greetingMessage,
                prompt_text: promptText,
                manual_content: manualContent,
                stt_provider: sttProvider || null,
                variable_values: variableValues,
                sources: selectedSources.map(s => ({
                    source_type: s.source_type,
                    source_id: s.source_id,
                    source_title: s.source_title,
                    content: s.content || '',
                    character_count: s.character_count || 0,
                })),
            };

            const response = await axiosInstance.post(`/api/chatbots/${chatbotId}/live-conversation`, payload);

            if (response.data?.success) {
                setMessage({ type: 'success', text: 'Live conversation settings saved successfully.' });
            } else {
                setMessage({ type: 'error', text: response.data?.message || 'Failed to save settings.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'An error occurred while saving.' });
        } finally {
            setSaving(false);
        }
    };

    // Permission checks
    if (checkingPermissions) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canView) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mt: 6 }}>
                    <strong>Access Denied</strong><br />
                    You don't have permission to view Live Conversation settings. Please contact your administrator.
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading settings...</Typography>
            </Box>
        );
    }

    const getSourceTypeIcon = (type) => {
        switch (type) {
            case 'website': return <WebIcon fontSize="small" />;
            case 'document': return <DocIcon fontSize="small" />;
            case 'gdrive': return <DriveIcon fontSize="small" />;
            default: return <EditIcon fontSize="small" />;
        }
    };

    // ---- Step / wizard navigation + validation ----
    // General (greeting + prompt) is required; Variables is optional. The
    // Knowledge step is gated only by the character budget.
    const isLastStep = tab === STEP_LABELS.length - 1;
    const goNext = () => setTab((t) => Math.min(STEP_LABELS.length - 1, t + 1));
    const goBack = () => setTab((t) => Math.max(0, t - 1));
    const generalValid = !!(greetingMessage || '').trim() && !!(promptText || '').trim();
    const mandatoryValid = generalValid;
    const overBudget = !ragEnabled && totalCharacters > MAX_CHARACTERS;
    const stepValid = (t) => (t === 0 ? generalValid : true);
    // Later steps are reachable only once the mandatory General step is valid
    // (read-only users may browse freely). Keeps tabs in sync with Next so users
    // can't jump past the incomplete required step.
    const canGoToStep = (i) => (!canModify || i === 0) ? true : generalValid;
    const saveDisabled = saving || !mandatoryValid || overBudget;
    const forwardBlocked = canModify && (isLastStep ? (!mandatoryValid || overBudget) : !stepValid(tab));
    const forwardHint = !forwardBlocked
        ? null
        : isLastStep
            ? (overBudget
                ? 'Reduce the Knowledge content below the character limit to finish.'
                : 'Complete the General step (greeting & prompt) to finish.')
            : 'Complete the required fields (*) to continue.';

    // The forward button: "Next" on intermediate steps, "End Setup" (saves) on the last.
    const renderForward = () => (
        isLastStep ? (
            canModify ? (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <DoneIcon />}
                    onClick={handleSave}
                    disabled={saveDisabled}
                >
                    {saving ? 'Saving…' : 'End Setup'}
                </Button>
            ) : null
        ) : (
            <Button variant="contained" size="small" endIcon={<NextIcon />} onClick={goNext} disabled={canModify && !stepValid(tab)}>
                Next
            </Button>
        )
    );

    const stepNavTop = (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                        Step {tab + 1} of {STEP_LABELS.length}
                    </Typography>
                    {forwardHint && (
                        <Typography variant="caption" color="warning.main">{forwardHint}</Typography>
                    )}
                </Box>
                {renderForward()}
            </Box>
            <Divider sx={{ mb: 2 }} />
        </>
    );

    const stepNavBottom = (
        <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Button variant="text" size="small" startIcon={<BackIcon />} disabled={tab === 0} onClick={goBack}>
                    Back
                </Button>
                {renderForward()}
            </Box>
        </>
    );

    return (
        <Box sx={{ maxWidth: 980, mx: 'auto', pb: 2 }}>
            {/* Header + step tabs */}
            <Box
                sx={{
                    mb: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 2 }, py: 1.25, flexWrap: 'wrap' }}>
                    <Box
                        sx={{
                            width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: 'rgba(41,121,255,0.12)', color: 'primary.main',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <RecordVoiceOverIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                            Live Conversation
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            In-page voice chat configuration
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ToolIcon />}
                        onClick={() => navigate(`/chatbot/${urlChatbotId || chatbotId}/tool-call`)}
                    >
                        Tools
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LogsIcon />}
                        onClick={() => navigate(`/chatbot/${urlChatbotId || chatbotId}/call-logs?channel=live-conversation`)}
                    >
                        Logs
                    </Button>
                    {canModify && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saveDisabled}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    )}
                </Box>
                <Divider />
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ px: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}
                >
                    <Tab label="1. General" />
                    <Tab label="2. Knowledge & sources" disabled={!canGoToStep(1)} />
                    <Tab label="3. Variables" disabled={!canGoToStep(2)} />
                </Tabs>
            </Box>

            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {/* Step 1 — General: voice provider + greeting + prompt */}
            {tab === 0 && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        {stepNavTop}

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            <FormControl fullWidth size="small" disabled={!canModify}>
                                <InputLabel id="lc-stt-provider-label">Voice Provider</InputLabel>
                                <Select
                                    labelId="lc-stt-provider-label"
                                    value={sttProvider}
                                    label="Voice Provider"
                                    onChange={(e) => setSttProvider(e.target.value)}
                                >
                                    <MenuItem value=""><em>Use default (server setting)</em></MenuItem>
                                    <MenuItem value="google">Google Cloud TTS</MenuItem>
                                    <MenuItem value="gemini">Gemini LLM</MenuItem>
                                    <MenuItem value="live">Gemini Live</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Greeting message"
                                value={greetingMessage}
                                onChange={(e) => setGreetingMessage(e.target.value)}
                                placeholder="Hello, how can I help you?"
                                disabled={!canModify}
                                inputProps={{ maxLength: 1024 }}
                                helperText={`${greetingMessage.length} / 1,024 — spoken when the conversation starts`}
                            />
                        </Box>

                        <TextField
                            sx={{ mt: 2 }}
                            fullWidth
                            size="small"
                            required
                            multiline
                            minRows={4}
                            maxRows={12}
                            label="Prompt instructions"
                            value={promptText}
                            onChange={(e) => setPromptText(e.target.value)}
                            placeholder="Enter your prompt instructions here…"
                            disabled={!canModify}
                            helperText={<span>Use <code>{'{{variable_name}}'}</code> placeholders to inject values from the Variables step.</span>}
                        />

                        {stepNavBottom}
                    </CardContent>
                </Card>
            )}

            {/* Step 2 — Knowledge & sources: RAG toggle gates the source picker. */}
            {tab === 1 && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        {stepNavTop}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Default knowledgebase (RAG)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {ragEnabled
                                        ? 'On — answers from your trained data. Turn off to use the custom sources below.'
                                        : 'Off — the chatbot uses only the sources & manual content below.'}
                                </Typography>
                            </Box>
                            <Switch
                                checked={ragEnabled}
                                onChange={(e) => setRagEnabled(e.target.checked)}
                                disabled={!canModify}
                                color="primary"
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {ragEnabled ? (
                            <Alert severity="info" variant="outlined">
                                Custom sources & manual content are used only when the default knowledgebase (RAG)
                                is off. Turn it off above to configure them.
                            </Alert>
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">Content budget</Typography>
                                    <Typography
                                        variant="caption"
                                        color={totalCharacters > MAX_CHARACTERS ? 'error' : 'text.secondary'}
                                        fontWeight={600}
                                    >
                                        {totalCharacters.toLocaleString()} / {MAX_CHARACTERS.toLocaleString()}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={characterPercentage}
                                    color={totalCharacters > MAX_CHARACTERS ? 'error' : characterPercentage > 80 ? 'warning' : 'primary'}
                                    sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Sources: {selectedSourcesChars.toLocaleString()} ({selectedSources.length} items)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Manual: {manualContentLength.toLocaleString()}
                                    </Typography>
                                </Box>

                                {/* Selected sources */}
                                {selectedSources.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {selectedSources.map((src, idx) => (
                                            <Chip
                                                key={`${src.source_type}-${src.source_id}-${idx}`}
                                                icon={getSourceTypeIcon(src.source_type)}
                                                label={`${src.source_title?.substring(0, 40)}${src.source_title?.length > 40 ? '…' : ''} (${(src.character_count || 0).toLocaleString()})`}
                                                onDelete={canModify ? () => handleRemoveSource(src.source_type, src.source_id) : undefined}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}

                                {/* Source type tabs */}
                                <Tabs
                                    value={sourceTab}
                                    onChange={(e, v) => { setSourceTab(v); setSearchText(''); }}
                                    variant="fullWidth"
                                    sx={{ mb: 1.5, minHeight: 40 }}
                                >
                                    {sourceTabLabels.map((label, idx) => (
                                        <Tab key={label} icon={sourceTabIcons[idx]} label={label} iconPosition="start" sx={{ minHeight: 40 }} />
                                    ))}
                                </Tabs>

                                {/* Search */}
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={`Search ${sourceTabLabels[sourceTab]}…`}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    sx={{ mb: 1.5 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Source list */}
                                {loadingSources ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : availableSources.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No trained {sourceTabLabels[sourceTab].toLowerCase()} data found for this chatbot.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                            {availableSources.map((source) => {
                                                const selected = isSourceSelected(source.source_type, source.source_id);
                                                return (
                                                    <Box
                                                        key={`${source.source_type}-${source.source_id}`}
                                                        onClick={() => canModify && handleToggleSource(source)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 1,
                                                            py: 0.75,
                                                            borderBottom: '1px solid',
                                                            borderColor: 'divider',
                                                            '&:last-of-type': { borderBottom: 'none' },
                                                            backgroundColor: selected ? 'action.selected' : 'transparent',
                                                            '&:hover': { backgroundColor: 'action.hover' },
                                                            cursor: canModify ? 'pointer' : 'default',
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={selected}
                                                            disabled={!canModify}
                                                            size="small"
                                                        />
                                                        <Typography variant="body2" noWrap title={source.source_title} sx={{ flex: 1, minWidth: 0 }}>
                                                            {source.source_title}
                                                        </Typography>
                                                        <Chip
                                                            label={`${(source.character_count || 0).toLocaleString()}`}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        {/* Pagination */}
                                        {totalSourcePage > 1 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1.5 }}>
                                                <Button
                                                    size="small"
                                                    disabled={sourcePage <= 1}
                                                    onClick={() => fetchAvailableSources(sourcePage - 1)}
                                                >
                                                    Previous
                                                </Button>
                                                <Typography variant="caption">
                                                    Page {sourcePage} of {totalSourcePage}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    disabled={sourcePage >= totalSourcePage}
                                                    onClick={() => fetchAvailableSources(sourcePage + 1)}
                                                >
                                                    Next
                                                </Button>
                                            </Box>
                                        )}
                                    </>
                                )}

                                {/* Manual content */}
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                                    Manual content
                                </Typography>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <TiptapEditor editor={editor} height="200px" />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {manualContentLength.toLocaleString()} characters
                                </Typography>
                            </>
                        )}

                        {stepNavBottom}
                    </CardContent>
                </Card>
            )}

            {/* Step 3 — Variables (VAPI-style {{var}} substitution) */}
            {tab === 2 && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        {stepNavTop}

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <VarIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600}>Variables</Typography>
                            <Box sx={{ flex: 1 }} />
                            {canModify && (
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setVariableRows([...variableRows, { key: '', value: '' }])}
                                >
                                    Add
                                </Button>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: variableRows.length ? 1.5 : 0 }}>
                            Key/value pairs that replace <code>{'{{key}}'}</code> placeholders in the prompt and tool body templates at runtime.
                        </Typography>

                        {variableRows.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">No variables defined.</Typography>
                        ) : variableRows.map((row, idx) => (
                            <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 1, mb: 1 }}>
                                <TextField
                                    size="small"
                                    label="Key"
                                    value={row.key}
                                    onChange={(e) => {
                                        const next = [...variableRows];
                                        next[idx] = { ...next[idx], key: e.target.value };
                                        setVariableRows(next);
                                    }}
                                    placeholder="location_name"
                                    disabled={!canModify}
                                />
                                <TextField
                                    size="small"
                                    label="Value"
                                    value={row.value}
                                    onChange={(e) => {
                                        const next = [...variableRows];
                                        next[idx] = { ...next[idx], value: e.target.value };
                                        setVariableRows(next);
                                    }}
                                    placeholder="Mundelein Dental Clinic"
                                    disabled={!canModify}
                                />
                                <IconButton
                                    onClick={() => setVariableRows(variableRows.filter((_, i) => i !== idx))}
                                    disabled={!canModify}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}

                        {stepNavBottom}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
